import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { NzAutocompleteModule, NzAutocompleteOptionComponent } from 'ng-zorro-antd/auto-complete';
import { NzInputModule } from 'ng-zorro-antd/input';

import { ProfileResponse } from '@smarthead-api/models/profile-response';
import { ProfileService } from '@smarthead-api/services/profile.service';
import { SearchService } from '@smarthead-api/services/search.service';

import { ToastService } from '../../services/toast.service';

const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const PROFILE_ID_PATTERN = /^\d+$/;

/** An option of the search: a found profile, or the profile with the typed ID */
type ProfileSearchOption = { kind: 'profile'; profile: ProfileResponse } | { kind: 'id'; id: number };

/**
 * Search of a profile by its name, or by its ID (only digits typed). Emits the picked profile and clears itself.
 */
@Component({
  selector: 'sh-profile-search',
  templateUrl: './profile-search.component.html',
  styleUrl: './profile-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzAutocompleteModule,
    NzInputModule
  ]
})
export class ProfileSearchComponent {
  readonly #searchService = inject(SearchService);
  readonly #profileService = inject(ProfileService);
  readonly #toast = inject(ToastService);
  readonly #destroyRef = inject(DestroyRef);

  readonly placeholder = input('Search profile by name or ID');
  /** Note shown after a found profile, e.g. its consolidation */
  readonly optionNote = input<(profile: ProfileResponse) => string | null>(() => null);
  readonly size = input<'default' | 'large'>('default');

  readonly profileSelected = output<ProfileResponse>();

  protected readonly text = signal('');
  protected readonly options = signal<ProfileSearchOption[]>([]);
  protected readonly searching = signal(false);

  readonly #search$ = new Subject<string>();

  constructor() {
    this.#search$
      .pipe(
        map(text => text.trim()),
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(() => this.searching.set(true)),
        switchMap(text => this.#findOptions(text)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(options => {
        this.options.set(options);
        this.searching.set(false);
      });
  }

  protected onTextChange(text: unknown): void {
    // picking an option writes the option itself to the model, it's handled by `onOptionSelected`
    if (typeof text !== 'string') {
      return;
    }

    this.text.set(text);
    this.#search$.next(text);
  }

  protected onOptionSelected(option: NzAutocompleteOptionComponent): void {
    const selected = option.nzValue as ProfileSearchOption;

    // the input shows the typed text again, the search starts from scratch
    setTimeout(() => this.#reset());

    if (selected.kind === 'profile') {
      this.profileSelected.emit(selected.profile);
      return;
    }

    this.#profileService.getProfileById({ id: selected.id }).subscribe({
      next: profile => this.profileSelected.emit(profile),
      error: (error: unknown) => this.#toast.error(error)
    });
  }

  #findOptions(text: string): Observable<ProfileSearchOption[]> {
    const idOption: ProfileSearchOption[] = PROFILE_ID_PATTERN.test(text) ? [{ kind: 'id', id: Number(text) }] : [];

    if (text.length < SEARCH_MIN_LENGTH) {
      return of(idOption);
    }

    return this.#searchService.searchProfile({ search: text }).pipe(
      map(profiles => [
        ...idOption,
        ...profiles.map(profile => ({ kind: 'profile', profile }) as const)
      ]),
      catchError((error: unknown) => {
        console.error(error);
        return of(idOption);
      })
    );
  }

  #reset(): void {
    this.text.set('');
    this.options.set([]);
    this.#search$.next('');
  }
}
